import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { GenericCell } from '../../templates/tables/Cells.js';
import * as con from '../../GlobalConstants';
import './GridTable.css';
import '../../templates/tables/Table.css';
import { RefGridStrategyHook } from '../hooks/gridStrategyHooks.js';
import { RefModelParameterHook, StateModelParameterHook } from '../hooks/ModelParameterHooks.js';
import { setManualOverride, clearInstrumentOverrides } from '../../store/slices/gridSlice.js';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';

// =====================================================
// CONSTANTS
// =====================================================

const STEPS   = [con.STEP_2, con.STEP_3, con.STEP_4];
const COLUMNS = [0, 1, 2, 3];

const LEVELS_CONFIG = [
  { label: 'Superior', index: 1, evenRow: true  },
  { label: 'Inferior', index: 0, evenRow: false },
];

const PRODUCTS = [
  { key: 'spot',    constant: con.SPOT,    label: 'Spot',     amountCurrency: con.MONEY_COP, frequencyCurrency: con.MONEY_COP, isSpot: true  },
  { key: 'fwds',    constant: con.FWDS,    label: 'Forward',  amountCurrency: con.MONEY_COP, frequencyCurrency: con.MONEY_COP, isSpot: false },
  { key: 'options', constant: con.OPTIONS, label: 'Collares', amountCurrency: con.MONEY_USD, frequencyCurrency: con.MONEY_COP, isSpot: false },
];

const getIsEvenRow = (rowIndex, productKey) => {
  switch (productKey) {
    case 'spot':    return rowIndex % 2 !== 0;
    case 'fwds':    return rowIndex % 2 === 0;
    case 'options': return rowIndex % 2 !== 0;
    default:        return rowIndex % 2 !== 0;
  }
};

const getProductRowClass = (rowIndex, productKey) =>
  getIsEvenRow(rowIndex, productKey) ? 'even-row' : 'uneven-row';

// =====================================================
// COMPONENT
// =====================================================

const GridTable = () => {

  const dispatch = useDispatch();

  // ── Store: grid strategy ──────────────────────────
  const isAutomatic     = RefModelParameterHook(con.AUTOMATIC_GRID);
  const gridStrategy    = RefGridStrategyHook(con.GRID_STRATEGY);
  const gridLevels      = RefGridStrategyHook(con.GRID_LEVELS);
  const manualOverrides = RefGridStrategyHook(con.MANUAL_OVERRIDES);

  // ── Store: model parameters ───────────────────────
  const [canSpot,                setCanSpot               ] = StateModelParameterHook(con.CAN_SPOT);
  const [canFwds,                setCanFwds               ] = StateModelParameterHook(con.CAN_FWDS);
  const [canOptions,             setCanOptions            ] = StateModelParameterHook(con.CAN_OPTIONS);
  const [consideredSpotCoverage, setConsideredSpotCoverage] = StateModelParameterHook(con.CONSIDERED_SPOT_COVERAGE);

  // ── Local state ───────────────────────────────────
  const [amountActive, setAmountActive] = useState({ spot: true, fwds: true, options: true });

  // ── Ensure at least one product active ───────────
  useEffect(() => {
    if (!canFwds && !canOptions && !canSpot) setCanSpot(true);
  }, [canFwds, canOptions, canSpot, setCanSpot]);

  // ── Derived maps ──────────────────────────────────
  const canMap = useMemo(() => ({
    spot: canSpot, fwds: canFwds, options: canOptions,
  }), [canSpot, canFwds, canOptions]);

  const setCanMap = useMemo(() => ({
    spot: setCanSpot, fwds: setCanFwds, options: setCanOptions,
  }), [setCanSpot, setCanFwds, setCanOptions]);

  const keyToConstant = useMemo(() => ({
    spot: con.SPOT, fwds: con.FWDS, options: con.OPTIONS,
  }), []);

  // ── Helpers ───────────────────────────────────────
  // Display priority: the operator's pending manual edit wins over the value
  // that came back in the last strategy response.
  const getStepValue = useCallback((step, productConstant, field) => {
    const override = manualOverrides?.[`${step}.${productConstant}`];
    if (!isAutomatic && override && override.field === field) {
      return override.value;
    }
    return gridStrategy?.[step]?.[productConstant]?.[field] ?? '-';
  }, [gridStrategy, manualOverrides, isAutomatic]);

  const isEditing = useCallback((productKey) =>
    !isAutomatic && canMap[productKey]
  , [isAutomatic, canMap]);

  const isToggleDisabled = useCallback((productKey) =>
    isAutomatic || !canMap[productKey]
  , [isAutomatic, canMap]);

  const toggleAmountActive = useCallback((productKey, isSpotFreq = false) => {
    if (isAutomatic) return;
    if (!canMap[productKey]) return;
    if (isSpotFreq && !consideredSpotCoverage) return;
    // Flipping Monto/Frecuencia: stale overrides of the other field for this
    // instrument must not be sent to the backend
    dispatch(clearInstrumentOverrides(keyToConstant[productKey]));
    setAmountActive(prev => ({ ...prev, [productKey]: !prev[productKey] }));
  }, [isAutomatic, canMap, consideredSpotCoverage, dispatch, keyToConstant]);

  const handleAmountChange = useCallback((step, productConstant, productKey, value) => {
    if (!isEditing(productKey)) return;
    dispatch(setManualOverride({
      step, instrument: productConstant, field: con.AMOUNT, value,
    }));
  }, [isEditing, dispatch]);

  const handleFreqChange = useCallback((step, productConstant, productKey, value) => {
    if (!isEditing(productKey)) return;
    dispatch(setManualOverride({
      step, instrument: productConstant, field: con.FREQUENCY, value,
    }));
  }, [isEditing, dispatch]);

  // ── Product section renderer ──────────────────────
  const renderProductSection = useCallback((product) => {
    const { key, constant, label, amountCurrency, frequencyCurrency, isSpot } = product;
    const canTrade       = canMap[key];
    const editing        = isEditing(key);
    const isAmountActive = amountActive[key];
    const toggleDisabled = isToggleDisabled(key);

    const spotFreqDisabled = isSpot
      ? (isAutomatic || !canSpot || !consideredSpotCoverage)
      : toggleDisabled;

    if (!canTrade) {
      return (
        <div key={key} className="financial-products-section">
          <table className="product-table">
            <tbody>
              <tr className={getProductRowClass(1, key)}>
                <td rowSpan={1} className={`vertical-title ${getProductRowClass(1, key)}`}>
                  {label}
                </td>
                <td className={getProductRowClass(1, key)}>
                  <GenericCell evenRow={getIsEvenRow(1, key)} cellType={con.TEXT} editing={false} value="¿Pueden?" />
                </td>
                <td className={getProductRowClass(1, key)}>
                  <Toggle
                    checked={canTrade}
                    onChange={(e) => setCanMap[key](e.target.checked)}
                    disabled={isSpot ? (!canFwds && !canOptions) : false}
                    icons={false}
                    className="custom-toggle"
                  />
                </td>
                <td colSpan={2} className={getProductRowClass(1, key)} />
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div key={key} className="financial-products-section">
        <table className="product-table">
          <tbody>
            <tr className={getProductRowClass(1, key)}>
              <td rowSpan={3} className={`vertical-title ${getProductRowClass(1, key)}`}>
                {label}
              </td>
              <td className={getProductRowClass(1, key)}>
                <GenericCell evenRow={getIsEvenRow(1, key)} cellType={con.TEXT} editing={false} value="¿Pueden?" />
              </td>
              <td className={getProductRowClass(1, key)}>
                <Toggle
                  checked={canTrade}
                  onChange={(e) => setCanMap[key](e.target.checked)}
                  disabled={isSpot ? (!canFwds && !canOptions) : false}
                  icons={false}
                  className="custom-toggle"
                />
              </td>
              {isSpot ? (
                <>
                  <td className={getProductRowClass(1, key)}>
                    <GenericCell evenRow={getIsEvenRow(1, key)} cellType={con.TEXT} editing={false} value="¿Cuenta como cob?" />
                  </td>
                  <td className={getProductRowClass(1, key)}>
                    <Toggle
                      checked={consideredSpotCoverage}
                      onChange={(e) => canSpot && setConsideredSpotCoverage(e.target.checked)}
                      disabled={!canSpot}
                      icons={false}
                      className="custom-toggle"
                    />
                  </td>
                </>
              ) : (
                <td colSpan={2} className={getProductRowClass(1, key)} />
              )}
            </tr>

            <tr className={getProductRowClass(2, key)}>
              <td style={{ display: 'flex', alignItems: 'center', gap: '1px' }} className={getProductRowClass(2, key)}>
                <Toggle
                  checked={isAmountActive}
                  onChange={() => toggleAmountActive(key)}
                  disabled={toggleDisabled}
                  icons={false}
                  className="custom-toggle"
                />
                <GenericCell evenRow={getIsEvenRow(2, key)} cellType={con.TEXT} editing={false} value="Monto" />
              </td>
              {STEPS.map((step) => (
                <td key={step}>
                  <div className="grid-celda flex items-center gap-2">
                    <GenericCell
                      evenRow={getIsEvenRow(2, key)}
                      cellType={con.MONEY}
                      currency={amountCurrency}
                      editing={editing && isAmountActive && canTrade}
                      value={getStepValue(step, constant, con.AMOUNT)}
                      setValue={(val) => handleAmountChange(step, constant, key, val)}
                    />
                  </div>
                </td>
              ))}
            </tr>

            <tr className={getProductRowClass(3, key)}>
              <td style={{ display: 'flex', alignItems: 'center', gap: '1px' }} className={getProductRowClass(3, key)}>
                <Toggle
                  checked={!isAmountActive}
                  onChange={() => toggleAmountActive(key, isSpot)}
                  disabled={spotFreqDisabled}
                  icons={false}
                  className="custom-toggle"
                />
                <GenericCell evenRow={getIsEvenRow(3, key)} cellType={con.TEXT} editing={false} value="Frecuencia" />
              </td>
              {STEPS.map((step) => (
                <td key={step}>
                  <div className="grid-celda flex items-center gap-2">
                    <GenericCell
                      evenRow={getIsEvenRow(3, key)}
                      cellType={con.MONEY}
                      currency={frequencyCurrency}
                      editing={editing && !isAmountActive && canTrade && (!isSpot || consideredSpotCoverage)}
                      value={
                        manualOverrides?.[`${step}.${constant}`]?.field === con.FREQUENCY
                          ? getStepValue(step, constant, con.FREQUENCY)
                          : (Number(gridStrategy?.[step]?.[constant]?.[con.AMOUNT] ?? 0) === 0
                              ? ''
                              : getStepValue(step, constant, con.FREQUENCY))
                      }
                      setValue={(val) => handleFreqChange(step, constant, key, val)}
                    />
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }, [canMap, setCanMap, isEditing, isToggleDisabled, toggleAmountActive,
      amountActive, isAutomatic, canSpot, canFwds, canOptions,
      consideredSpotCoverage, setConsideredSpotCoverage, manualOverrides,
      gridStrategy, getStepValue, handleAmountChange, handleFreqChange]);

  // ── Render ────────────────────────────────────────
  return (
    <>
      <table className="nonFixedTable">
        <thead>
          <tr className="header-row">
            <th>ESCALÓN</th>
            {COLUMNS.map((col) => <th key={col}>{col + 1}</th>)}
          </tr>
        </thead>
        <tbody>
          {LEVELS_CONFIG.map(({ label, index, evenRow }) => (
            <tr key={label} className={evenRow ? 'even-row' : 'uneven-row'}>
              <td>
                <GenericCell evenRow={evenRow} cellType={con.TEXT} editing={false} value={label} />
              </td>
              {COLUMNS.map((col) => (
                <td key={col}>
                  <GenericCell
                    evenRow={evenRow}
                    cellType={con.MONEY}
                    currency={con.MONEY_COP}
                    editing={false}
                    value={gridLevels?.[`step_${col + 1}`]?.[index] ?? '-'}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {PRODUCTS.map(renderProductSection)}
      <br></br>
      <br></br>
    </>
  );
};

export default GridTable;